/**
 * ChatPage - Main Chat Orchestrator
 * ==================================
 * 
 * Refactored from Chat.js (~2000 lines → ~300 lines)
 * 
 * This is a thin orchestrator that composes:
 * - ChatHeader
 * - MessageList  
 * - ChatComposer
 * - ChatMenu
 * 
 * All logic is in hooks:
 * - useChatMessages: message CRUD
 * - useChatUIState: UI state
 * - useEntitlements: feature gating
 * - useTypingIndicator: typing status
 * - useReadReceipts: read receipts
 * - useAudioRecorder: voice messages
 * 
 * Non-negotiables preserved:
 * ✓ Textarea sizing/autogrow behavior
 * ✓ Send/mic/button alignment (no drift)
 * ✓ No horizontal scroll regression
 * ✓ Message list rendering and ordering
 * ✓ POST → GET → setMessages pattern
 * ✓ data-testid selectors for Playwright
 */
import React, { useContext, useEffect, useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiClient from "../../api/client";
import { AuthContext } from '../../App';
import { useEntitlements } from '../../context/EntitlementsContext';

// Extracted components
import { ChatHeader } from './ChatHeader.jsx';
import { MessageList } from './MessageList.jsx';
import { ChatComposer } from './ChatComposer.jsx';
import { ChatMenu } from './ChatMenu.jsx';

// Custom hooks
import { useChatMessages } from '../../hooks/useChatMessages';
import { useChatUIState } from '../../hooks/useChatUIState';
import { useTypingIndicator } from '../../hooks/useTypingIndicator';
import { useReadReceipts } from '../../hooks/useReadReceipts';
import { useAudioRecorder } from '../../hooks/useAudioRecorder';

// Existing components/utilities
import ReportBlockModal from '../ReportBlockModal';
import VideoLimitModal from '../VideoLimitModal';
import ComposerAssist from './ComposerAssist';
import InnerWorkModal from '../InnerWorkModal';
import { requestVideoCallStart } from '../../services/chat';
import { reportUser, blockUser } from '../../api/moderation';
import { grantPrivateGalleryAccess } from '../../api/privateGallery';
import { getTodayPrompt } from '../../api/innerWork';
import { trackEvent } from '../../api/analytics';
import { isEligibleForNudge, markNudgeShown, markNudgeUsed } from './assistNudge';
import { toast } from 'sonner';


export function ChatPage() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useContext(AuthContext);
  const { hasFeature, getThreshold } = useEntitlements();
  
  // Entitlements-based feature checks
  const canSendVoice = hasFeature('can_send_voice');
  const canUseVideoChat = hasFeature('can_video_chat');
  const videoUnlockThreshold = getThreshold('video_unlock_min_messages_each');
  
  // Match data
  const [match, setMatch] = useState(null);
  const [videoUnlocked, setVideoUnlocked] = useState(false);
  const [otherOnline, setOtherOnline] = useState(false);
  
  // Custom hooks
  const {
    messages, loading: messagesLoading, error: messagesError, sending,
    sendMessage, sendAudioMessage, refreshMessages, addReaction, removeReaction,
  } = useChatMessages(matchId, token);
  
  const {
    newMessage, setNewMessage, clearMessage,
    showMoreMenu, toggleMenu, closeMenu,
    reactionUI, openReactionPicker, closeReactionPicker,
    videoInviteSent, setVideoInviteSent,
    isStartingVideo, setIsStartingVideo,
    showReport, setShowReport,
    showVideoLimitModal, setShowVideoLimitModal,
    showInnerWorkModal, setShowInnerWorkModal,
    innerWorkPrompt, setInnerWorkPrompt,
    showInnerNudge, setShowInnerNudge,
    isGrantingPrivateAccess, setIsGrantingPrivateAccess,
    hasPrivateGalleryAccess, setHasPrivateGalleryAccess,
  } = useChatUIState();
  
  // Get other user ID
  const partnerId = match?.user_ids?.find(id => id !== user?.id);
  
  // Typing indicator
  const { isPartnerTyping, sendTyping } = useTypingIndicator(matchId, partnerId, token);
  
  // Read receipts
  useReadReceipts(matchId, messages, token);
  
  // Audio recorder
  const {
    isRecording, recordingDuration, audioError,
    startRecording, stopRecording, cancelRecording,
  } = useAudioRecorder();
  
  // Fetch match data
  useEffect(() => {
    if (!matchId || !token) return;
    
    const fetchMatch = async () => {
      try {
        const response = await apiClient.get(
          `/matches/${matchId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMatch(response.data);
      } catch (err) {
        console.error('[ChatPage] Failed to fetch match:', err);
      }
    };
    
    fetchMatch();
  }, [matchId, token]);
  
  // Check video unlock status
  useEffect(() => {
    if (!matchId || !token || !canUseVideoChat) return;
    
    const checkVideoUnlock = async () => {
      try {
        const response = await apiClient.get(
          `/chat/${matchId}/video-unlock-status`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setVideoUnlocked(response.data?.video_unlocked ?? false);
      } catch (err) {
        console.error('[ChatPage] Video unlock check failed:', err);
      }
    };
    
    checkVideoUnlock();
  }, [matchId, token, canUseVideoChat, messages.length]);
  
  // Check Inner Work nudge eligibility
  useEffect(() => {
    if (!user?.id || !isEligibleForNudge(user.id)) return;
    
    getTodayPrompt(token)
      .then(prompt => {
        if (prompt) {
          setInnerWorkPrompt(prompt);
          setShowInnerNudge(true);
          markNudgeShown(user.id);
        }
      })
      .catch(err => console.error('[ChatPage] Inner work prompt error:', err));
  }, [user?.id, token, setInnerWorkPrompt, setShowInnerNudge]);
  
  // Handle send message
  const handleSendMessage = useCallback(async (e) => {
    e?.preventDefault();
    if (!newMessage.trim() || sending) return;
    
    const text = newMessage.trim();
    clearMessage();
    
    const result = await sendMessage(text);
    if (!result.success) {
      toast.error(result.error || 'Failed to send message');
      setNewMessage(text); // Restore on failure
    }
  }, [newMessage, sending, clearMessage, sendMessage, setNewMessage]);
  
  // Handle voice message
  const handleVoiceClick = useCallback(async () => {
    if (!canSendVoice) {
      toast.info('Verify your identity to send voice notes');
      return;
    }
    
    if (isRecording) {
      const { blob, durationMs } = await stopRecording();
      if (blob) {
        const result = await sendAudioMessage(blob, durationMs);
        if (!result.success) {
          toast.error(result.error || 'Failed to send voice message');
        }
      }
    } else {
      startRecording();
    }
  }, [canSendVoice, isRecording, stopRecording, startRecording, sendAudioMessage]);
  
  // Handle video invite
  const handleVideoInvite = useCallback(async () => {
    if (!canUseVideoChat) {
      toast.info('Elite subscription required for video chat');
      return;
    }
    
    if (!videoUnlocked) {
      toast.info(`Send ${videoUnlockThreshold} messages each to unlock video`);
      return;
    }
    
    setIsStartingVideo(true);
    try {
      const result = await requestVideoCallStart(matchId, token);
      if (result.success) {
        setVideoInviteSent(true);
        trackEvent('video_call_started', { match_id: matchId });
        
        // Navigate to the video screen
        navigate(`/video/${matchId}`);
      } else if (result.reason === 'limit_reached') {
        setShowVideoLimitModal(true);
      } else {
        toast.error(result.error || 'Could not start video call');
      }
    } catch (err) {
      console.error('Failed to start video call:', err);
      toast.error(
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        'Could not start video call. Please try again.'
      );
    } finally {
      setIsStartingVideo(false);
    }
  }, [canUseVideoChat, videoUnlocked, videoUnlockThreshold, matchId, token, setIsStartingVideo, setVideoInviteSent, setShowVideoLimitModal, navigate]);
  
  // Handle reaction toggle
  const handleReactionToggle = useCallback((messageId, emoji) => {
    const message = messages.find(m => m.id === messageId);
    // 🎯 PATCH: Defensive check - ensure reactions is an array before calling .some()
    const reactions = Array.isArray(message?.reactions) ? message.reactions : [];
    const hasReacted = reactions.some(r => r.emoji === emoji && r.user_id === user?.id);
    
    if (hasReacted) {
      removeReaction(messageId, emoji);
    } else {
      addReaction(messageId, emoji);
    }
  }, [messages, user?.id, addReaction, removeReaction]);
  
  // Handle private gallery share
  const handleSharePrivateGallery = useCallback(async () => {
    if (!partnerId) return;
    
    setIsGrantingPrivateAccess(true);
    try {
      await grantPrivateGalleryAccess(partnerId);
      setHasPrivateGalleryAccess(true);
      toast.success('Private gallery access granted');
      closeMenu();
    } catch (err) {
      toast.error('Failed to share gallery');
    } finally {
      setIsGrantingPrivateAccess(false);
    }
  }, [partnerId, setIsGrantingPrivateAccess, setHasPrivateGalleryAccess, closeMenu]);
  
  // Handle report
  const handleReport = useCallback(() => {
    closeMenu();
    setShowReport(true);
  }, [closeMenu, setShowReport]);
  
  // Handle block
  const handleBlock = useCallback(async () => {
    if (!partnerId || !token) return;
    
    try {
      await blockUser(partnerId, token);
      toast.success('User blocked');
      navigate('/inbox');
    } catch (err) {
      toast.error('Failed to block user');
    }
  }, [partnerId, token, navigate]);
  
  // Typing handler
  const handleMessageChange = useCallback((text) => {
    setNewMessage(text);
    sendTyping();
  }, [setNewMessage, sendTyping]);
  
  // Inner work nudge action
  const handleInnerNudgeUse = useCallback(() => {
    if (user?.id) {
      markNudgeUsed(user.id);
    }
    setShowInnerWorkModal(true);
    setShowInnerNudge(false);
  }, [user?.id, setShowInnerWorkModal, setShowInnerNudge]);
  
  return (
    <div 
      className="flex flex-col h-screen bg-gray-50"
      data-testid="chat-page"
    >
      {/* Header */}
      <ChatHeader
        match={match}
        otherOnline={otherOnline}
        isVideoAvailable={true}
        canUseVideoChat={canUseVideoChat}
        videoUnlocked={videoUnlocked}
        videoInviteSent={videoInviteSent}
        isStartingVideo={isStartingVideo}
        onVideoInvite={handleVideoInvite}
        onBack={() => navigate('/inbox')}
        onInboxClick={() => navigate('/inbox')}
        onMenuToggle={toggleMenu}
        showMoreMenu={showMoreMenu}
        menuContent={
          <ChatMenu
            show={showMoreMenu}
            onClose={closeMenu}
            onSharePrivateGallery={handleSharePrivateGallery}
            onReport={handleReport}
            onBlock={handleBlock}
            isGrantingPrivateAccess={isGrantingPrivateAccess}
            hasPrivateGalleryAccess={hasPrivateGalleryAccess}
          />
        }
      />
      
      {/* Message List */}
      <MessageList
        messages={messages}
        currentUserId={user?.id}
        matchProfile={match?.profile}
        loading={messagesLoading}
        loadError={messagesError}
        onReactionPickerOpen={(msg, e) => openReactionPicker({ ...msg, isOwnMessage: msg.sender_id === user?.id }, e)}
        reactionUI={reactionUI}
        onReactionToggle={handleReactionToggle}
        onReactionPickerClose={closeReactionPicker}
      />
      
      {/* Composer */}
      <ChatComposer
        newMessage={newMessage}
        onMessageChange={handleMessageChange}
        onSendMessage={handleSendMessage}
        sending={sending}
        canSendVoice={canSendVoice}
        isRecording={isRecording}
        recordingDuration={recordingDuration}
        audioError={audioError}
        onVoiceClick={handleVoiceClick}
        onCancelRecording={cancelRecording}
        isPartnerTyping={isPartnerTyping}
        canShowInnerNudge={showInnerNudge && innerWorkPrompt}
        innerNudgeContent={
          showInnerNudge && innerWorkPrompt ? (
            <div className="px-2 py-2 mb-2 bg-purple-50 border border-purple-200 rounded-lg">
              <div className="text-xs text-purple-700 font-medium mb-1">
                🧘 Today&apos;s reflection
              </div>
              <button
                onClick={handleInnerNudgeUse}
                className="text-sm text-purple-600 hover:text-purple-800"
              >
                {innerWorkPrompt.question?.substring(0, 50)}...
              </button>
            </div>
          ) : null
        }
        assistContent={
          <ComposerAssist
            matchId={matchId}
            onSelect={(text) => setNewMessage(prev => prev + text)}
          />
        }
      />
      
      {/* Modals */}
      {showReport && (
        <ReportBlockModal
          targetUserId={partnerId}
          onClose={() => setShowReport(false)}
          onReportSubmit={async (reason) => {
            await reportUser(partnerId, reason, token);
            toast.success('Report submitted');
            setShowReport(false);
          }}
        />
      )}
      
      {showVideoLimitModal && (
        <VideoLimitModal onClose={() => setShowVideoLimitModal(false)} />
      )}
      
      {showInnerWorkModal && innerWorkPrompt && (
        <InnerWorkModal
          prompt={innerWorkPrompt}
          onClose={() => setShowInnerWorkModal(false)}
        />
      )}
    </div>
  );
}

export default ChatPage;
